if (!customElements.get('product-media-gallery')) {
  customElements.define(
    'product-media-gallery',
    class ProductMediaGallery extends HTMLElement {
      constructor() {
        super();

        this.CLASSES = {
          activeItem: 'product-media-gallery__item--is-active',
          isZoomed: 'main-product--media-gallery-zoom',
          slideshow: 'product-media-gallery--slideshow',
        };

        this.mainContainer = document.querySelector('[js-product-media-gallery-container]');
        this.bulletsContainer = this.querySelector('.product-media-gallery__bullets');
        /**
         *  @type {HTMLElement | null}
         */
        this.slideIndicator = this.querySelector('.product-media-gallery__slide-indicator');

        /**
         *  @type {HTMLElement | null}
         */
        this.itemsContainer = this.querySelector('.product-media-gallery__items');
        this.images = this.itemsContainer?.querySelectorAll('img');
        this.touchstartX = 0;

        this.controls.zoom?.addEventListener('click', this.handleZoom.bind(this));

        window.innerWidth > 992 && document.addEventListener('keydown', this.handleKeydown.bind(this));

        this.itemsContainer?.addEventListener('scroll', this.handleSlideIndicator.bind(this));

        document.addEventListener('productMediaGalleryUpdate', () => {
          this.setFirstImage.bind(this)();
          this.createBullets.bind(this)();

          if (this.visibleImages) {
            this.visibleImages.length <= 1 ? this.hideControls() : this.showControls();
          }
        });

        if (!this.classList.contains(this.CLASSES.slideshow)) return;

        this.controls.next && this.controls.next.addEventListener('click', this.setNextImage.bind(this));
        this.controls.previous && this.controls.previous.addEventListener('click', this.setPreviousImage.bind(this));

        this.createBullets();
      }

      setNextImage() {
        if (!this.currentImage?.nextElementSibling) return;

        let nextImage = this.currentImage.nextElementSibling;

        while (nextImage && nextImage.getAttribute('aria-hidden') === 'true') {
          nextImage = nextImage.nextElementSibling;
        }

        nextImage ? this.setActiveImage(nextImage) : this.setActiveImage(this.firstImage);
      }

      setPreviousImage() {
        if (!this.currentImage) return;

        let previousImage = this.currentImage.previousElementSibling;

        while (previousImage && previousImage.getAttribute('aria-hidden') === 'true') {
          previousImage = previousImage.previousElementSibling;
        }

        previousImage ? this.setActiveImage(previousImage) : this.setActiveImage(this.lastImage);
      }

      setFirstImage() {
        if (!this.itemsContainer) return;

        const firstImage = this.itemsContainer.querySelector('[aria-hidden="false"]');

        this.setActiveImage(firstImage);

        this.itemsContainer.scrollTo({
          left: 0,
        });

        this.mainContainer?.classList.contains(this.CLASSES.isZoomed) && window.scrollTo({ top: 0 });
      }

      /**
       * @param {HTMLImageElement} image
       */
      setActiveImage(image) {
        if (!this.currentImage || !image) return;

        this.currentImage.classList.remove(this.CLASSES.activeItem);
        image.classList.add(this.CLASSES.activeItem);

        this.setActiveBullet();
      }

      setActiveBullet() {
        if (!this.bulletsContainer || !this.currentImage) return;

        this.bulletsContainer
          .querySelector('.product-media-gallery__bullet--is-active')
          ?.classList.remove('product-media-gallery__bullet--is-active');

        const index = this.currentImageIndex;
        if (index !== null && index >= 0) {
          this.bulletsContainer
            .querySelectorAll('.product-media-gallery__bullet')
            [index]?.classList.add('product-media-gallery__bullet--is-active');
        }
      }

      hideControls() {
        this.controls.next?.classList.add('util-hidden');
        this.controls.previous?.classList.add('util-hidden');
      }

      showControls() {
        this.controls.next?.classList.remove('util-hidden');
        this.controls.previous?.classList.remove('util-hidden');
      }

      handleZoom() {
        if (!this.mainContainer) return;

        if (this.mainContainer?.classList.contains(this.CLASSES.isZoomed)) {
          this.closeZoom();
        } else {
          this.mainContainer.classList.add(this.CLASSES.isZoomed);
          this.currentImage?.scrollIntoView();
        }
      }

      handleSlideIndicator() {
        if (!this.itemsContainer || !this.slideIndicator || !this.visibleImages?.length) return;

        const { scrollLeft, scrollWidth, clientWidth } = this.itemsContainer;
        const base = 1 / this.visibleImages.length;
        const progress = scrollWidth > clientWidth ? scrollLeft / (scrollWidth - clientWidth) : 1;

        this.slideIndicator.style.width = `${((base + (1 - base) * progress) * 100).toFixed(2)}%`;
      }

      /**
       * @param {KeyboardEvent} event
       */
      handleKeydown(event) {
        if (event.repeat) return;

        switch (event.key) {
          case 'Escape':
            this.closeZoom();
          default:
            break;
        }
      }

      closeZoom() {
        this.mainContainer?.classList.remove(this.CLASSES.isZoomed);
        window.scrollTo({ top: 0 });
      }

      createBullets() {
        if (!this.itemsContainer || !this.visibleImages || !this.bulletsContainer) return;

        const container = this.bulletsContainer;

        this.clearBullets();

        if (this.visibleImages.length <= 1) return;

        this.visibleImages.forEach((image, index) => {
          const bullet = document.createElement('button');

          bullet.classList.add('product-media-gallery__bullet', 'button');
          if (index === 0) {
            bullet.classList.add('product-media-gallery__bullet--is-active');
          }

          bullet.setAttribute('aria-label', `Image ${index + 1}`);

          bullet.addEventListener('click', () => {
            this.setActiveImage(image);

            container
              .querySelector('.product-media-gallery__bullet--is-active')
              ?.classList.remove('product-media-gallery__bullet--is-active');
            bullet.classList.add('product-media-gallery__bullet--is-active');
          });

          container.appendChild(bullet);
        });
      }

      clearBullets() {
        if (!this.bulletsContainer) return;

        this.bulletsContainer.replaceChildren();
      }

      get isTouchDevice() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      }

      get controls() {
        return {
          next: this.querySelector('.product-media-gallery__control--next'),
          previous: this.querySelector('.product-media-gallery__control--previous'),
          zoom: this.querySelector('.product-media-gallery__control--zoom'),
        };
      }

      /**
       * @returns {HTMLElement | null}
       */
      get currentImage() {
        if (!this.itemsContainer) return null;

        return this.itemsContainer.querySelector(`.${this.CLASSES.activeItem}`);
      }

      get currentImageIndex() {
        if (!this.currentImage || !this.visibleImages) return null;

        return this.images ? Array.from(this.visibleImages).indexOf(this.currentImage) : -1;
      }

      get firstImage() {
        if (!this.itemsContainer) return;

        return this.itemsContainer.querySelector('[aria-hidden="false"]');
      }

      get lastImage() {
        if (!this.itemsContainer) return;

        const lastImage = this.visibleImages[this.visibleImages.length - 1];

        return lastImage;
      }

      get visibleImages() {
        if (!this.itemsContainer) return;

        return this.itemsContainer.querySelectorAll('[aria-hidden="false"]');
      }

      get visibleImageIndex() {
        if (!this.currentImage) return;

        return this.visibleImages ? Array.from(this.visibleImages).indexOf(this.currentImage) : -1;
      }
    },
  );
}
